import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, CalendarDays, MapPin, Star, Users } from "lucide-react";
import { PublicNav } from "@/components/site/PublicNav";
import { Footer } from "@/components/site/Footer";
import { SocialFeed } from "@/components/social/SocialFeed";
import { FollowButton } from "@/components/social/FollowButton";
import { getPublicProfile } from "@/lib/social.functions";
import { PublicBadgeGrid } from "@/components/achievements/PublicBadgeGrid";
import { useAuthState } from "@/hooks/use-auth-state";
import { ukDate } from "@/lib/format";

export const Route = createFileRoute("/u/$username")({
  head: ({ params }) => ({
    meta: [
      { title: `@${params.username} on Shields` },
      {
        name: "description",
        content: `See @${params.username}'s Shields profile — posts, finished work, reviews and achievements.`,
      },
      { property: "og:title", content: `@${params.username} on Shields` },
      {
        property: "og:description",
        content: "Posts, finished work, reviews and achievements on Shields.",
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const { username } = Route.useParams();
  const { userId } = useAuthState();
  const { data, isLoading } = useQuery({
    queryKey: ["public-profile", username, userId ?? "anon"],
    queryFn: () => getPublicProfile({ data: { username, viewerId: userId } }),
  });

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">We couldn't find @{username}.</p>
        <Link to="/community" className="font-semibold text-primary underline">
          Browse the community
        </Link>
      </div>
    );
  }

  const name = data.displayName ?? `@${username}`;
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "S";

  return (
    <div className="min-h-screen">
      <PublicNav />
      <main className="pb-16">
        {/* Banner */}
        <div className="relative h-44 w-full bg-[image:var(--gradient-premium)] sm:h-60">
          {data.bannerUrl && (
            <img src={data.bannerUrl} alt="" className="h-full w-full object-cover" />
          )}
        </div>

        <div className="section-pad mx-auto -mt-14 max-w-5xl">
          <div className="card-surface p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-end gap-4">
                <span className="-mt-16 block h-28 w-28 shrink-0 overflow-hidden rounded-full border-4 border-card bg-[image:var(--gradient-premium)] shadow-[var(--shadow-glow)]">
                  {data.avatarUrl ? (
                    <img src={data.avatarUrl} alt={`${name} profile photo`} className="h-full w-full object-cover" />
                  ) : (
                    <span className="grid h-full w-full place-items-center font-display text-3xl font-extrabold text-primary-foreground">
                      {initials}
                    </span>
                  )}
                </span>
                <div>
                  <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold sm:text-3xl">
                    {name}
                    {data.isPartner && <BadgeCheck className="h-6 w-6 text-success" aria-hidden />}
                  </h1>
                  <p className="text-sm font-semibold text-primary">@{data.username}</p>
                  {data.headline && <p className="mt-1 text-sm font-semibold">{data.headline}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <FollowButton userId={data.userId} initialFollowing={data.isFollowing} />
                {data.isPartner && (
                  <Link
                    to="/partners/$partnerId"
                    params={{ partnerId: data.userId }}
                    className="btn-secondary-hero"
                  >
                    Work &amp; prices
                  </Link>
                )}
              </div>
            </div>

            {data.bio && <p className="mt-5 whitespace-pre-wrap text-sm text-muted-foreground">{data.bio}</p>}

            <div className="mt-5 flex flex-wrap gap-6 border-t border-border pt-5 text-sm">
              <Stat label="Posts" value={data.counts.posts} />
              <Stat label="Followers" value={data.counts.followers} />
              <Stat label="Following" value={data.counts.following} />
              {data.ratingCount > 0 && data.ratingAvg !== null && (
                <span className="flex items-center gap-1.5 font-semibold">
                  <Star className="h-4 w-4 fill-warning text-warning" aria-hidden />
                  {data.ratingAvg.toFixed(1)}
                  <span className="font-normal text-muted-foreground">({data.ratingCount} reviews)</span>
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" aria-hidden /> Member since {ukDate(data.memberSince)}
              </span>
              {data.serviceAreas && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" aria-hidden /> {data.serviceAreas}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" aria-hidden /> {data.isPartner ? "Verified partner" : "Customer"}
              </span>
            </div>
          </div>

          <div className="mt-10">
            <PublicBadgeGrid
              userId={data.userId}
              audience={data.isPartner ? "partner" : "customer"}
              heading="Achievements"
            />
          </div>

          <div className="mt-10">
            <SocialFeed authorId={data.userId} showComposer={false} heading="Posts" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <span className="font-semibold">
      {value.toLocaleString("en-GB")} <span className="font-normal text-muted-foreground">{label}</span>
    </span>
  );
}
